/**
 * CORE-1790 — обычные эмодзи с клавиатуры → картинки комплекта.
 *
 * Таблица алиасов согласована продуктом 20.10.2026 (Вероника, по уточнениям
 * Марии): несколько Unicode-символов одного смысла сопоставлены одному
 * `agape_N`. Это правило для нового ввода — набранное или вставленное с
 * телефонной клавиатуры после отправки показывается фирменной картинкой и
 * хранится токеном `:agape_N:` (см. `utils/emojiTokens`). К сохранённым слагам
 * старых реакций (`LEGACY_REACTION_TYPES`) таблица отношения не имеет.
 *
 * Разбор идёт целыми последовательностями: селекторы начертания (FE0E/FE0F),
 * тон кожи (1F3FB–1F3FF) и ZWJ-связки с полом. Сначала вся последовательность
 * (❤️‍🔥 → agape_4), и только потом её часть (❤️ → agape_1); тон кожи и пол
 * отдельного смысла не дают. Последовательность с незнакомым хвостом
 * (❤️‍🚀) остаётся как есть целиком — обрывков ZWJ в тексте не бывает.
 * Символов вне таблицы (⚡, 👍, 😂) замена не касается.
 *
 * Без React и RN: один и тот же разбор у веба и нативных сборок.
 */

/** Слаг комплекта → обычные эмодзи того же смысла (порядок — как в таблице продукта). */
export const EMOJI_ALIASES: Readonly<Record<string, readonly string[]>> = {
  // Любовь, тепло — наградная
  agape_1: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🤎', '🖤', '🤍', '🩷', '🩵', '🩶', '♥️', '💖', '💗', '💓', '💕', '💞', '❣️', '🫶'],
  // Баланс, гармония — наградная
  agape_2: ['☯️', '⚖️'],
  // Благодарность, просьба
  agape_3: ['🙏', '🤲'],
  // Огонь, горящее сердце
  agape_4: ['🔥', '❤️‍🔥'],
  // Разбитое сердце, боль
  agape_5: ['💔', '❤️‍🩹'],
  // Прозрение, «смотрю»
  agape_6: ['👁️', '👀', '💡'],
  // Покой, практика, медитация
  agape_7: ['🧘', '🧘‍♀️', '🧘‍♂️', '😌', '🕊️', '🌱'],
  // Agape, свет, вдохновение
  agape_8: ['✨', '🌟', '💫', '⭐', '🌞'],
  // Объятия, поддержка — наградная
  agape_9: ['🤗', '🫂'],
  // Нет, против, несогласие, протест, злость
  agape_10: ['🙅', '🙅‍♀️', '🙅‍♂️', '❌', '🚫', '👎', '😠', '😡', '🤬'],
  // Грусть, слёзы
  agape_11: ['😢', '😭', '😥', '😓', '😞', '😔', '☹️', '🙁', '🥺'],
};

/** Селекторы начертания и тон кожи — не меняют смысл, при сравнении отбрасываются. */
const STRIP_RE = /\uFE0E|\uFE0F|\u{1F3FB}|\u{1F3FC}|\u{1F3FD}|\u{1F3FE}|\u{1F3FF}/gu;
const ZWJ = '\u200D';

/** Последовательность без селекторов и тона кожи — ключ таблицы. */
export function normalizeEmoji(sequence: string): string {
  return sequence.replace(STRIP_RE, '');
}

const LOOKUP = new Map<string, string>();
const BASES = new Set<string>();
for (const [type, aliases] of Object.entries(EMOJI_ALIASES)) {
  for (const alias of aliases) {
    const key = normalizeEmoji(alias);
    LOOKUP.set(key, type);
    for (const cp of key) if (cp !== ZWJ) BASES.add(cp);
  }
}

const escapeCodePoint = (cp: string) => `\\u{${(cp.codePointAt(0) as number).toString(16)}}`;
const BASE = `(?:${Array.from(BASES).map(escapeCodePoint).join('|')})`;
const MODIFIERS = '(?:[\\uFE0E\\uFE0F]|[\\u{1F3FB}-\\u{1F3FF}])*';

/**
 * Кластер из символов таблицы: базовый символ с модификаторами и любые
 * ZWJ-продолжения из таких же символов. Символы вне таблицы не совпадают,
 * поэтому ⚡ или 🚀 регулярка не трогает.
 */
export const EMOJI_CLUSTER_RE = new RegExp(`${BASE}${MODIFIERS}(?:${ZWJ}${BASE}${MODIFIERS})*`, 'gu');

/** Слаг комплекта для обычного эмодзи (целой последовательности) или undefined. */
export function reactionTypeOfUnicode(sequence: string): string | undefined {
  return LOOKUP.get(normalizeEmoji(sequence.trim()));
}

/** Токен комплекта в тексте — тот же вид, что у `utils/emojiTokens`. */
const TOKEN_RE = /:(agape_(?:[1-9]|1[01])):/g;

/**
 * Обычный символ того же смысла, которым остаётся недоступный токен: первый
 * алиас таблицы (❤️ у agape_1, ☯️ у agape_2, 🤗 у agape_9) — он же запасной
 * символ комплекта.
 */
export function unicodeOfReactionType(type: string): string | undefined {
  return EMOJI_ALIASES[type]?.[0];
}

/**
 * Замена обычных эмодзи в тексте токенами комплекта. `canUse(type)` решает,
 * можно ли человеку этот слаг: закрытую наградную картинку (1/2/9) Unicode не
 * обходит — символ остаётся обычным, серверный reward gate не трогается.
 *
 * CORE-1790 (владелец, 02.11.2026): то же правило для токена, набранного кодом
 * или вставленного в черновик (`:agape_1:`): наградные 1/2/9 уходят фирменными
 * только при действительном unlock аккаунта, иначе токен становится обычным
 * символом того же смысла — текст не теряется, награда не обходится.
 */
export function brandifyEmoji(text: string, canUse: (type: string) => boolean = () => true): string {
  if (!text) return text;
  const branded = text.replace(EMOJI_CLUSTER_RE, (match: string, offset: number, whole: string) => {
    const end = offset + match.length;
    // Связка продолжается незнакомым символом (❤️‍🚀) или начинается после
    // него — это одна чужая последовательность, рвать её нельзя.
    if (whole.charCodeAt(end) === 0x200d || (offset > 0 && whole.charCodeAt(offset - 1) === 0x200d)) return match;
    const type = LOOKUP.get(normalizeEmoji(match));
    if (!type || !canUse(type)) return match;
    return `:${type}:`;
  });
  return branded.replace(TOKEN_RE, (token: string, type: string) => {
    if (canUse(type)) return token;
    return unicodeOfReactionType(type) ?? token;
  });
}
